import { isByokModel, type ModelCatalogEntry } from 'models'
import { computed, inject, provide, type InjectionKey, type Ref } from 'vue'

/**
 * The organization's model catalog, offered once by the app and read by every
 * chip that shows a model.
 *
 * Provided rather than passed down, because the chip appears in places a prop
 * cannot reach without threading it through half the component tree: the
 * footer of every chat message, the document preview, the agent tree, the
 * pickers. Before the catalog existed each of those derived the name from the
 * id and looked up an i18n label, which is exactly the duplication the
 * catalog removes; without an injection they would all have shown a raw id.
 *
 * A surface with no provider, the embeddable widget, falls back to the id. It
 * has no organization to ask and no catalog to read.
 */
export const MODEL_CATALOG_KEY: InjectionKey<
    Readonly<Ref<ModelCatalogEntry[] | undefined>>
> = Symbol('modelCatalog')

/** `undefined` until the catalog is known: loading, or failed. */
export const provideModelCatalog = (
    catalog: Readonly<Ref<ModelCatalogEntry[] | undefined>>,
) => provide(MODEL_CATALOG_KEY, catalog)

/** One entry by id, or undefined where no catalog was provided. */
export const useModelCatalogEntry = () => {
    const catalog = inject(MODEL_CATALOG_KEY, undefined)
    return (id?: string): ModelCatalogEntry | undefined =>
        id ? catalog?.value?.find((entry) => entry.id === id) : undefined
}

/**
 * Whether there is a catalog to judge a model by: not in the widget, which
 * has none, and not while the app's is still loading.
 */
export const useModelCatalogKnown = () => {
    const catalog = inject(MODEL_CATALOG_KEY, undefined)
    return computed(() => catalog?.value !== undefined)
}

/**
 * The note of a model a known catalog does not have. For an endpoint model
 * it means the endpoint no longer has it (taken out of its list, or the
 * endpoint deleted), and the runtime refuses it. A gateway id outside the
 * curated list is not marked: the gateway still answers it.
 */
export const missingModelNote = (
    id?: string,
): { label: string; hint: string } | undefined =>
    isByokModel(id)
        ? { label: 'label.modelNotAvailable', hint: 'hint.modelNotOnEndpoint' }
        : undefined

/**
 * The note a model carries where it is shown, when the organization cannot
 * pick it: retired by the gateway, its whole source off, or switched off by
 * its administrator. One definition for the chip and the picker row, so
 * the two never say different things about the same model.
 */
export const modelAvailabilityNote = (
    entry: ModelCatalogEntry | undefined,
): { label: string; hint: string } | undefined => {
    if (!entry) {
        return undefined
    }
    if (entry.retired) {
        return { label: 'label.retired', hint: 'hint.modelRetired' }
    }
    // Its whole source is off, which stops the agents on it: said apart from
    // a model switched off on its own, which keeps answering them.
    if (entry.sourceOff) {
        return { label: 'label.modelNotAvailable', hint: 'hint.modelSourceOff' }
    }
    if (entry.disabled) {
        return {
            label: 'label.modelSwitchedOff',
            hint: 'hint.modelDisabledForOrganization',
        }
    }
    return undefined
}
